// src/pages/Login.js
import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Alert, Card, ProgressBar } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext'; 
import PhoneNumberInput from '../components/PhoneNumberInput';
import './Login.css'; // custom styling for extra sleekness

const BACKEND_URL = process.env.REACT_APP_RXROOSTER_BACKEND_URL || "http://localhost:3001";

const Login = () => {
  const [phone, setPhone] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [phoneValid, setPhoneValid] = useState(false);
  const [codeValid, setCodeValid] = useState(false);
  const navigate = useNavigate();

  // Validate verification code when it changes
  useEffect(() => {
    // Simple validation - code should be 6 digits
    setCodeValid(verificationCode.length >= 4 && /^\d+$/.test(verificationCode));
  }, [verificationCode]);

  const sendCode = async () => {
    setErrorMsg('');
    if (!phoneValid) {
      setErrorMsg('Please enter a valid phone number before requesting a verification code.');
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/auth/send-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone }),
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to send verification code. Please try again.');
      }
      
      setCodeSent(true);
    } catch (error) {
      setErrorMsg(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const { login: authLogin } = useAuth();

  const login = async () => {
    setErrorMsg('');
    if (!codeValid) {
      setErrorMsg('Please enter a valid verification code (at least 4 digits).');
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/auth/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, code: verificationCode }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Login failed.');

      console.log('Login response:', data); // For debugging

      // Check if this is a new user that needs registration
      if (data.newUser === true) {
        // Show notification that user needs to register first
        setErrorMsg('You need to register an account before logging in. Redirecting to registration...');
        
        // Wait 2 seconds to show the message before redirecting
        setTimeout(() => {
          // Redirect to registration page with phone pre-filled
          navigate('/register', { state: { prefillPhone: data.phone } });
        }, 2000);
        
        return;
      }

      console.log('User role from API:', data.user?.role);
      console.log('Token:', data.token ? 'Token received' : 'No token!');
      
      authLogin(data.token); // use context method here
      
      // Navigate based on user role
      const userRole = data.user?.role;
      
      if (userRole === 'ADMIN') {
        console.log('Navigating to /admin');
        navigate('/admin');
      } else if (userRole === 'GROUP') {
        console.log('Navigating to /group-intake');
        navigate('/group-intake');
      } else { // CRNA role or default
        console.log('Navigating to /profile');
        navigate('/profile');
      }
      
    } catch (error) {
      setErrorMsg(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container className="d-flex justify-content-center align-items-center vh-100">
      <Card className="p-4 shadow-sm login-card" style={{ maxWidth: '450px', width: '100%' }}>
        <Card.Body>
          <h2 className="text-center mb-4">Welcome Back</h2>
          {errorMsg && <Alert variant="danger">{errorMsg}</Alert>}
          
          {!codeSent ? (
            <>
              <PhoneNumberInput
                value={phone}
                onChange={setPhone}
                required={true}
                id="loginPhone"
                label="Enter your phone number"
                onValidityChange={setPhoneValid}
                inputProps={{ 
                  autoFocus: true,
                  "aria-describedby": "phoneHelpBlock" 
                }}
              />
              
              <Form.Text id="phoneHelpBlock" className="text-muted mb-3">
                Enter your US phone number to receive a verification code.
              </Form.Text>
              
              <Button
                variant="primary"
                className="w-100 mt-2"
                onClick={sendCode}
                disabled={isLoading || !phoneValid}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Sending Code...
                  </>
                ) : (
                  'Send Verification Code'
                )}
              </Button>
            </>
          ) : (
            <>
              {isLoading && <ProgressBar animated now={100} className="mb-3" />}
              
              <Form.Group className="mb-3">
                <Form.Label>Enter Verification Code</Form.Label>
                <Form.Control
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder="123456"
                  className={verificationCode ? (codeValid ? 'is-valid' : 'is-invalid') : ''}
                  autoFocus
                  maxLength={6}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
                <Form.Text>
                  Enter the code sent to your phone ({phone})
                </Form.Text>
              </Form.Group>
              
              <div className="d-flex justify-content-between mb-3">
                <Button
                  variant="outline-secondary"
                  onClick={() => {
                    setCodeSent(false);
                    setVerificationCode('');
                  }}
                >
                  Back
                </Button>
                <Button
                  variant="success"
                  onClick={login}
                  disabled={isLoading || !codeValid}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Verifying...
                    </>
                  ) : (
                    'Login'
                  )}
                </Button>
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default Login;
